import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { ChevronLeft, ChevronDown, ChevronUp, Check, Star, Coffee, Leaf, Gift, Sparkles, Calendar, Clock, Info } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/styles/brand";

// ═══════════════════════════════════════════════════════════
// FARBEN - Unified via brand.js
// ═══════════════════════════════════════════════════════════
const C = BRAND.colors;
const COLORS = {
  background: C.bg,
  green: C.green,
  greenLight: C.greenLight,
  orange: C.orange,
  orangeLight: C.orangeLight,
  text: C.text,
  textMuted: C.textMuted,
  white: C.textLight,
  cream: C.bgSecondary,
  yellow: C.tanLight,
  matcha: C.greenLight,
};

// ═══════════════════════════════════════════════════════════
// MEMBERSHIP DATEN - PREMIUM CLUB MODELL
// ═══════════════════════════════════════════════════════════
const MEMBERSHIPS = {
  matcha: {
    id: "matcha",
    name: "Franky's Matcha Club",
    shortName: "Matcha Club",
    emoji: "🍵",
    price: 29.90,
    color: COLORS.matcha,
    tagline: "Dein wöchentliches Matcha-Ritual – mit 10% Rabatt auf alles.",
    taglineDE: "Dein wöchentliches Matcha-Ritual – mit 10% Rabatt auf alles.",
    // Was enthalten ist
    includedBenefits: [
      { icon: "🍵", text: "1 Basic Matcha jede Woche gratis", textDE: "1 Basic Matcha jede Woche gratis" },
      { icon: "💚", text: "Standardgröße & Rezept inklusive", textDE: "Standardgröße & Rezept inklusive" },
      { icon: "💰", text: "10% Rabatt auf alle Speisen & Drinks", textDE: "10% Rabatt auf alle Speisen & Drinks" },
      { icon: "🎂", text: "Geburtstagsgeschenk", textDE: "Geburtstagsgeschenk" },
      { icon: "🌟", text: "Früher Zugang zu neuen Specials", textDE: "Früher Zugang zu neuen Specials" },
      { icon: "🏪", text: "Einlösbar in allen Filialen", textDE: "Einlösbar in allen Filialen" },
    ],
    // Upgrades nicht inklusive
    upgradeNote: "Alternative milk & syrups are not included – available at a small surcharge.",
    upgradeNoteDE: "Alternative Milch & Sirup sind nicht inklusive – gegen kleinen Aufpreis.",
    highlight: "Beliebteste Wahl",
    highlightDE: "Beliebteste Wahl",
  },
  coffee: {
    id: "coffee",
    name: "Franky's Coffee Pass",
    shortName: "Coffee Pass",
    emoji: "☕",
    price: 19.90,
    color: COLORS.orange,
    tagline: "Dein tägliches Kaffee-Ritual mit besonderen Vorteilen.",
    taglineDE: "Dein tägliches Kaffee-Ritual mit besonderen Vorteilen.",
    // Was enthalten ist
    includedBenefits: [
      { icon: "☕", text: "1 basic coffee every day", textDE: "1 Basic Coffee jeden Tag" },
      { icon: "📋", text: "Espresso, Americano, Cappuccino, Small Latte", textDE: "Espresso, Americano, Cappuccino, kleiner Latte" },
      { icon: "🥛", text: "Standardmilch inklusive", textDE: "Standardmilch inklusive" },
      { icon: "🏪", text: "Available at all locations", textDE: "Einlösbar in allen Filialen" },
    ],
    // Upgrades verfügbar
    upgradeNote: "Alternative milk and flavor add-ons available as upgrades.",
    upgradeNoteDE: "Alternative Milch und Sirup-Add-ons als Upgrades verfügbar.",
    highlight: null,
  },
  pasta: {
    id: "pasta",
    name: "Franky's Pasta Flat",
    shortName: "Pasta Flat",
    emoji: "🍝",
    price: 32.99,
    color: COLORS.green,
    tagline: "4× Pasta im Monat – egal welche.",
    taglineDE: "4× Pasta im Monat – egal welche.",
    // Ersparnis-Rechnung: 4 Pasta à 15,99€ = 63,96€ regulär → Flat 32,99€
    savings: {
      regularItemPrice: 15.99,
      itemsPerMonth: 4,
      regularTotal: 63.96,
      saveAmount: 30.97,
      effectivePerItem: 8.25,
      percentOff: 48,
    },
    // Was enthalten ist
    includedBenefits: [
      { icon: "🍝", text: "4 pasta dishes per month", textDE: "4 Pasta-Gerichte pro Monat gratis" },
      { icon: "🍽️", text: "Any pasta from the menu", textDE: "Egal welche Pasta von der Karte" },
      { icon: "🗓️", text: "Use them whenever you like", textDE: "Frei im Monat einlösbar – wann du willst" },
      { icon: "🏪", text: "Available in our Aachen store", textDE: "Einlösbar in unserer Aachener Filiale" },
    ],
    // Upgrades nicht inklusive
    upgradeNote: "Extra toppings and premium pasta available at a small surcharge.",
    upgradeNoteDE: "Extra-Toppings & Premium-Pasta gegen kleinen Aufpreis.",
    highlight: "Sattmacher",
    highlightDE: "Sattmacher",
  },
};

// ═══════════════════════════════════════════════════════════
// SÜSSE ILLUSTRATIONEN - WIE BESTEHENDE APP
// ═══════════════════════════════════════════════════════════

// Matcha Tasse mit Gesicht
const MatchaIllustration = ({ size = 80 }) => (
  <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
    {/* Dampf */}
    <motion.g animate={{ y: [0, -5, 0], opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }}>
      <path d="M35 15 Q37 8 35 2" stroke={COLORS.matcha} strokeWidth="2" fill="none" opacity="0.6"/>
      <path d="M50 12 Q52 5 50 0" stroke={COLORS.matcha} strokeWidth="2" fill="none" opacity="0.6"/>
      <path d="M65 15 Q67 8 65 2" stroke={COLORS.matcha} strokeWidth="2" fill="none" opacity="0.6"/>
    </motion.g>
    
    {/* Tasse */}
    <path d="M20 28 L25 85 Q50 95 75 85 L80 28 Z" fill={COLORS.matcha}/>
    <ellipse cx="50" cy="28" rx="32" ry="8" fill={COLORS.matcha}/>
    <ellipse cx="50" cy="28" rx="26" ry="5" fill="#9ED47A"/>
    
    {/* Matcha Schaum */}
    <ellipse cx="50" cy="32" rx="22" ry="4" fill="#B8E89A"/>
    
    {/* Süßes Gesicht */}
    <circle cx="38" cy="55" r="5" fill="#FFF"/>
    <circle cx="62" cy="55" r="5" fill="#FFF"/>
    <circle cx="38" cy="55" r="3" fill="#333"/>
    <circle cx="62" cy="55" r="3" fill="#333"/>
    <circle cx="36" cy="53" r="1.5" fill="#FFF"/>
    <circle cx="60" cy="53" r="1.5" fill="#FFF"/>
    
    {/* Lächeln */}
    <path d="M40 68 Q50 78 60 68" stroke="#FFF" strokeWidth="3" fill="none" strokeLinecap="round"/>
    
    {/* Wangen */}
    <circle cx="28" cy="62" r="5" fill="#9ED47A" opacity="0.5"/>
    <circle cx="72" cy="62" r="5" fill="#9ED47A" opacity="0.5"/>
    
    {/* Henkel */}
    <path d="M80 38 Q95 48 95 60 Q95 72 80 68" stroke={COLORS.matcha} strokeWidth="6" fill="none"/>
  </svg>
);

// Kaffee Tasse mit Gesicht
const CoffeeIllustration = ({ size = 80 }) => (
  <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
    {/* Dampf */}
    <motion.g animate={{ y: [0, -5, 0], opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}>
      <path d="M35 15 Q37 8 35 2" stroke={COLORS.orange} strokeWidth="2" fill="none" opacity="0.6"/>
      <path d="M50 12 Q52 5 50 0" stroke={COLORS.orange} strokeWidth="2" fill="none" opacity="0.6"/>
      <path d="M65 15 Q67 8 65 2" stroke={COLORS.orange} strokeWidth="2" fill="none" opacity="0.6"/>
    </motion.g>
    
    {/* Tasse */}
    <path d="M20 28 L25 85 Q50 95 75 85 L80 28 Z" fill={COLORS.orange}/>
    <ellipse cx="50" cy="28" rx="32" ry="8" fill={COLORS.orange}/>
    <ellipse cx="50" cy="28" rx="26" ry="5" fill={COLORS.orangeLight}/>
    
    {/* Kaffee */}
    <ellipse cx="50" cy="32" rx="22" ry="4" fill="#8B4513"/>
    
    {/* Süßes Gesicht */}
    <circle cx="38" cy="55" r="5" fill="#FFF"/>
    <circle cx="62" cy="55" r="5" fill="#FFF"/>
    <circle cx="38" cy="55" r="3" fill="#333"/>
    <circle cx="62" cy="55" r="3" fill="#333"/>
    <circle cx="36" cy="53" r="1.5" fill="#FFF"/>
    <circle cx="60" cy="53" r="1.5" fill="#FFF"/>
    
    {/* Lächeln */}
    <path d="M40 68 Q50 78 60 68" stroke="#FFF" strokeWidth="3" fill="none" strokeLinecap="round"/>
    
    {/* Wangen */}
    <circle cx="28" cy="62" r="5" fill={COLORS.orangeLight} opacity="0.5"/>
    <circle cx="72" cy="62" r="5" fill={COLORS.orangeLight} opacity="0.5"/>
    
    {/* Henkel */}
    <path d="M80 38 Q95 48 95 60 Q95 72 80 68" stroke={COLORS.orange} strokeWidth="6" fill="none"/>
  </svg>
);

// Pasta Teller mit Gesicht
const PastaIllustration = ({ size = 80 }) => (
  <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
    {/* Dampf */}
    <motion.g animate={{ y: [0, -5, 0], opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity, delay: 0.2 }}>
      <path d="M40 14 Q42 7 40 1" stroke={COLORS.green} strokeWidth="2" fill="none" opacity="0.6"/>
      <path d="M55 12 Q57 5 55 -1" stroke={COLORS.green} strokeWidth="2" fill="none" opacity="0.6"/>
      <path d="M65 15 Q67 8 65 2" stroke={COLORS.green} strokeWidth="2" fill="none" opacity="0.6"/>
    </motion.g>

    {/* Teller */}
    <ellipse cx="50" cy="62" rx="42" ry="20" fill={COLORS.green}/>
    <ellipse cx="50" cy="60" rx="42" ry="20" fill={COLORS.greenLight}/>
    <ellipse cx="50" cy="58" rx="32" ry="14" fill="#F4E4C1"/>

    {/* Spaghetti-Nester */}
    <path d="M28 56 Q40 48 52 56 Q64 64 74 54" stroke="#E8B84B" strokeWidth="3" fill="none" strokeLinecap="round"/>
    <path d="M30 60 Q42 52 54 60 Q66 68 72 58" stroke="#F0C75E" strokeWidth="3" fill="none" strokeLinecap="round"/>
    <path d="M32 52 Q44 60 56 52 Q66 46 70 56" stroke="#E8B84B" strokeWidth="2.5" fill="none" strokeLinecap="round"/>

    {/* Tomaten */}
    <circle cx="40" cy="56" r="3.5" fill="#E74C3C"/>
    <circle cx="60" cy="58" r="3.5" fill="#E74C3C"/>
    <circle cx="52" cy="52" r="2.5" fill="#C0392B"/>

    {/* Süßes Gesicht */}
    <circle cx="42" cy="40" r="4.5" fill="#FFF"/>
    <circle cx="58" cy="40" r="4.5" fill="#FFF"/>
    <circle cx="42" cy="40" r="2.5" fill="#333"/>
    <circle cx="58" cy="40" r="2.5" fill="#333"/>
    <circle cx="40.5" cy="38.5" r="1.2" fill="#FFF"/>
    <circle cx="56.5" cy="38.5" r="1.2" fill="#FFF"/>

    {/* Lächeln */}
    <path d="M44 47 Q50 53 56 47" stroke="#333" strokeWidth="2.5" fill="none" strokeLinecap="round"/>

    {/* Wangen */}
    <circle cx="34" cy="44" r="4" fill="#E74C3C" opacity="0.35"/>
    <circle cx="66" cy="44" r="4" fill="#E74C3C" opacity="0.35"/>
  </svg>
);

// VIP Krone mit Gesicht
const VIPIllustration = ({ size = 80 }) => (
  <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
    {/* Glitzer-Animation */}
    <motion.g animate={{ opacity: [0.3, 1, 0.3], scale: [0.9, 1.1, 0.9] }} transition={{ duration: 1.5, repeat: Infinity }}>
      <circle cx="20" cy="25" r="3" fill="#FFD700"/>
      <circle cx="80" cy="25" r="3" fill="#FFD700"/>
      <circle cx="50" cy="10" r="4" fill="#FFD700"/>
    </motion.g>
    
    {/* Krone */}
    <path d="M15 45 L25 20 L40 35 L50 10 L60 35 L75 20 L85 45 L80 80 L20 80 Z" fill="#9B59B6"/>
    <path d="M15 45 L25 20 L40 35 L50 10 L60 35 L75 20 L85 45" fill="none" stroke="#FFD700" strokeWidth="3"/>
    
    {/* Edelsteine */}
    <circle cx="25" cy="32" r="5" fill="#E74C3C"/>
    <circle cx="50" cy="22" r="6" fill="#3498DB"/>
    <circle cx="75" cy="32" r="5" fill="#2ECC71"/>
    
    {/* Sockel */}
    <rect x="20" y="75" width="60" height="10" rx="3" fill="#8E44AD"/>
    <rect x="22" y="78" width="56" height="4" fill="#FFD700" opacity="0.5"/>
    
    {/* Süßes Gesicht auf der Krone */}
    <circle cx="38" cy="55" r="4" fill="#FFF"/>
    <circle cx="62" cy="55" r="4" fill="#FFF"/>
    <circle cx="38" cy="55" r="2.5" fill="#333"/>
    <circle cx="62" cy="55" r="2.5" fill="#333"/>
    <circle cx="36" cy="53" r="1" fill="#FFF"/>
    <circle cx="60" cy="53" r="1" fill="#FFF"/>
    
    {/* Großes Lächeln */}
    <path d="M38 65 Q50 75 62 65" stroke="#FFF" strokeWidth="3" fill="none" strokeLinecap="round"/>
    
    {/* Glänzende Wangen */}
    <circle cx="30" cy="60" r="4" fill="#FFD700" opacity="0.4"/>
    <circle cx="70" cy="60" r="4" fill="#FFD700" opacity="0.4"/>
  </svg>
);

// ═══════════════════════════════════════════════════════════
// PREMIUM MEMBERSHIP CARD COMPONENT
// ═══════════════════════════════════════════════════════════
const MembershipCard = ({ membership, isSelected, onSelect, onJoin }) => {
  const m = MEMBERSHIPS[membership];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect && onSelect(m)}
      className={`relative p-5 rounded-3xl cursor-pointer transition-all`}
      style={{ 
        backgroundColor: COLORS.white,
        border: isSelected ? `2px solid ${m.color}` : `1px solid ${COLORS.cream}`,
        boxShadow: isSelected ? `0 8px 30px ${m.color}30` : "0 4px 20px rgba(0,0,0,0.06)"
      }}
      data-testid={`membership-card-${membership}`}
    >
      {/* Highlight Badge */}
      {m.highlight && (
        <div 
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold text-white whitespace-nowrap"
          style={{ backgroundColor: m.color }}
        >
          {m.highlightDE}
        </div>
      )}

      {/* Header mit Illustration */}
      <div className="flex items-center gap-4 mb-4">
        <motion.div
          animate={{ y: [0, -5, 0], rotate: [0, 3, -3, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          {membership === "matcha" ? (
            <MatchaIllustration size={60} />
          ) : membership === "pasta" ? (
            <PastaIllustration size={60} />
          ) : (
            <CoffeeIllustration size={60} />
          )}
        </motion.div>
        <div className="flex-1">
          <h3 className="text-lg font-bold" style={{ color: COLORS.text }}>{m.name}</h3>
          <p className="text-sm" style={{ color: COLORS.textMuted }}>{m.taglineDE}</p>
        </div>
      </div>

      {/* Preis - klar und prominent */}
      <div className="mb-4 p-3 rounded-2xl" style={{ backgroundColor: `${m.color}15` }}>
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-black" style={{ color: m.color }}>{m.price.toFixed(2).replace('.', ',')}€</span>
          <span className="text-sm" style={{ color: COLORS.textMuted }}>/ Monat</span>
        </div>
        {m.savings && (
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-sm line-through" style={{ color: COLORS.textMuted }}>
              {m.savings.regularTotal.toFixed(2).replace('.', ',')}€
            </span>
            <span className="text-xs font-bold" style={{ color: m.color }}>
              –{m.savings.percentOff}%
            </span>
          </div>
        )}
      </div>

      {/* Ersparnis-Highlight (nur bei Plänen mit savings, z.B. Pasta Flat) */}
      {m.savings && (
        <div
          className="mb-4 p-3 rounded-2xl flex items-center gap-3"
          style={{ backgroundColor: `${m.color}12`, border: `1px dashed ${m.color}55` }}
        >
          <span className="text-2xl">💸</span>
          <div className="flex-1">
            <p className="text-sm font-black" style={{ color: m.color }}>
              Du sparst {m.savings.saveAmount.toFixed(2).replace('.', ',')}€ jeden Monat
            </p>
            <p className="text-xs" style={{ color: COLORS.textMuted }}>
              Nur {m.savings.effectivePerItem.toFixed(2).replace('.', ',')}€ statt {m.savings.regularItemPrice.toFixed(2).replace('.', ',')}€ pro Pasta · schon ab der 3. Pasta lohnt es sich
            </p>
          </div>
        </div>
      )}

      {/* Was enthalten ist - KLAR kommuniziert */}
      <div className="mb-3">
        <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: m.color }}>
          Inklusive
        </p>
        <div className="space-y-2">
          {m.includedBenefits.map((benefit, i) => (
            <div key={i} className="flex items-center gap-2">
              <div 
                className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${m.color}20` }}
              >
                <Check className="w-3 h-3" style={{ color: m.color }} />
              </div>
              <span className="text-sm" style={{ color: COLORS.text }}>{benefit.textDE}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Upgrades - elegant und positiv formuliert */}
      <div 
        className="p-3 rounded-xl mt-3"
        style={{ backgroundColor: COLORS.cream }}
      >
        <div className="flex items-start gap-2">
          <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: m.color }} />
          <p className="text-xs" style={{ color: COLORS.textMuted }}>
            <span className="font-medium" style={{ color: COLORS.text }}>Extras & Upgrades verfügbar</span>
            <br />
            {m.upgradeNoteDE}
          </p>
        </div>
      </div>

      {/* CTA Button */}
      <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={(e) => {
          e.stopPropagation();
          onJoin(m);
        }}
        className="w-full mt-4 py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2"
        style={{ backgroundColor: m.color }}
        data-testid={`join-${membership}-btn`}
      >
        {m.shortName} beitreten
      </motion.button>

      {/* Selection Indicator */}
      {isSelected && (
        <div className="absolute top-4 right-4">
          <div 
            className="w-6 h-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: m.color }}
          >
            <Check className="w-4 h-4 text-white" />
          </div>
        </div>
      )}
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════
// MEMBER LUNCH PERKS - ALS ZUSATZ, NICHT ALS HAUPT-ABO
// ═══════════════════════════════════════════════════════════
const LunchPerksSection = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="p-4 rounded-2xl"
    style={{ backgroundColor: `${COLORS.green}08`, border: `1px dashed ${COLORS.green}30` }}
  >
    <div className="flex items-center gap-3 mb-3">
      <div 
        className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
        style={{ backgroundColor: `${COLORS.green}15` }}
      >
        🍝
      </div>
      <div>
        <p className="font-bold text-sm" style={{ color: COLORS.green }}>Mitglieder Lunch-Vorteile</p>
        <p className="text-xs" style={{ color: COLORS.textMuted }}>Zusätzliche Vorteile für alle Member</p>
      </div>
    </div>
    <div className="space-y-2 pl-13">
      {[
        "10% Rabatt auf ausgewählte Lunch-Gerichte",
        "Exklusive Pasta-Bundle-Angebote",
        "Rotierende Member-Only Lunch Specials"
      ].map((perk, i) => (
        <div key={i} className="flex items-center gap-2">
          <Check className="w-3 h-3 flex-shrink-0" style={{ color: COLORS.green }} />
          <span className="text-xs" style={{ color: COLORS.textMuted }}>{perk}</span>
        </div>
      ))}
    </div>
  </motion.div>
);

// ═══════════════════════════════════════════════════════════
// HOW MEMBERSHIP DRINKS WORK SECTION
// ═══════════════════════════════════════════════════════════
const HowItWorksSection = () => (
  <section className="px-5 py-6">
    <div className="p-5 rounded-2xl" style={{ backgroundColor: COLORS.white }}>
      <div className="flex items-center gap-2 mb-4">
        <Info className="w-5 h-5" style={{ color: COLORS.green }} />
        <h3 className="font-bold" style={{ color: COLORS.text }}>
          Einfach, transparent, für Stammgäste gemacht
        </h3>
      </div>
      
      <div className="space-y-4">
        {[
          {
            num: "1",
            title: "Dein Basic Drink ist inklusive",
            desc: "Deine Membership enthält einen klar definierten Basic-Drink-Vorteil."
          },
          {
            num: "2", 
            title: "Extras & Upgrades verfügbar",
            desc: "Personalisiere deinen Drink mit Premium-Add-ons wie alternativer Milch oder Sirup."
          },
          {
            num: "3",
            title: "Zusätzliche Mitglieder-Vorteile",
            desc: "Genieße Rabatte auf Food, Specials und exklusive Mitglieder-Belohnungen."
          }
        ].map((step, i) => (
          <div key={i} className="flex gap-3">
            <div 
              className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-white"
              style={{ backgroundColor: i === 0 ? COLORS.matcha : i === 1 ? COLORS.orange : COLORS.green }}
            >
              {step.num}
            </div>
            <div>
              <p className="font-medium text-sm" style={{ color: COLORS.text }}>{step.title}</p>
              <p className="text-xs" style={{ color: COLORS.textMuted }}>{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════
// FAQ COMPONENT
// ═══════════════════════════════════════════════════════════
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <motion.div
    className="rounded-2xl overflow-hidden"
    style={{ backgroundColor: COLORS.white }}
  >
    <button
      onClick={onClick}
      className="w-full p-4 flex items-center justify-between text-left"
    >
      <span className="font-medium text-sm pr-4" style={{ color: COLORS.text }}>{question}</span>
      {isOpen ? (
        <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: COLORS.textMuted }} />
      ) : (
        <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: COLORS.textMuted }} />
      )}
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="px-4 pb-4">
            <p className="text-sm" style={{ color: COLORS.textMuted }}>{answer}</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

// ═══════════════════════════════════════════════════════════
// MAIN MEMBERSHIP PAGE
// ═══════════════════════════════════════════════════════════
export default function MembershipPage() {
  const navigate = useNavigate();
  const { user, setShowAuthModal } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [openFAQ, setOpenFAQ] = useState(null);

  const handleJoin = (plan) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    navigate(`/membership/checkout/${plan.id}`);
  };

  // FAQ - Aktualisiert nach neuer Spezifikation
  const faqs = [
    // Matcha Club FAQs
    {
      q: "Was ist im wöchentlichen Basic Matcha enthalten?",
      a: "Dein Basic Matcha enthält einen Standard-Matcha in normaler Größe mit unserem klassischen Rezept und Standardmilch – perfekt für dein wöchentliches Ritual. Upgrades wie Hafermilch oder Sirup sind nicht inklusive und gegen kleinen Aufpreis verfügbar.",
    },
    {
      q: "Sind Upgrades (Hafermilch, Sirup) inklusive?",
      a: "Nein. Der kostenlose wöchentliche Matcha enthält Standardmilch und unser Basis-Rezept. Alternative Milch, Sirups und Extras sind gegen einen kleinen Aufpreis verfügbar.",
    },
    {
      q: "Bekomme ich Rabatt auf weitere Bestellungen?",
      a: "Ja! Als Matcha Club Member bekommst du 10% Rabatt auf alles – alle Speisen, Coffee Drinks, weitere Matcha-Getränke. Einfach beim Bezahlen deinen Member-QR-Code vorzeigen.",
    },
    {
      q: "Was passiert, wenn ich eine Woche vergesse, meinen Matcha einzulösen?",
      a: "Verfällt leider. Jede Woche hast du 1 Matcha gratis – wird er nicht eingelöst, wird er nicht in die nächste Woche übertragen. Es gibt immer genau 1 Matcha pro Woche.",
    },
    // Coffee Pass FAQs
    {
      q: "Welche Kaffee-Getränke sind im Coffee Pass enthalten?",
      a: "Im Coffee Pass sind enthalten: Espresso, Americano, Cappuccino oder kleiner Latte – jeden Tag eins deiner Wahl. Standardmilch inklusive.",
    },
    {
      q: "Ist Hafermilch oder Sirup beim Coffee Pass inklusive?",
      a: "Standardmilch ist inklusive. Alternative Milch (Hafer, Mandel, Kokos) und Sirup sind gegen kleinen Aufpreis verfügbar.",
    },
    // Allgemeine FAQs
    {
      q: "Wie funktioniert das Einlösen?",
      a: "Zeige einfach deinen persönlichen QR-Code in der App vor. Unser Team scannt ihn und dein Matcha bzw. Kaffee wird freigeschaltet. Funktioniert in allen Franky's Filialen.",
    },
    {
      q: "Wann startet mein Abo-Monat?",
      a: "Dein Abo startet am Tag der Buchung. Die monatliche Zahlung wird automatisch an diesem Tag abgebucht – kein Kalender-Monat, sondern dein persönlicher Start-Tag.",
    },
    {
      q: "Wie kann ich kündigen?",
      a: "Du kannst jederzeit in deinem Profil unter 'Abo verwalten' kündigen. Es gilt eine Kündigungsfrist von 3 Monaten – in dieser Zeit bleiben alle deine Vorteile voll aktiv.",
    },
    {
      q: "Was passiert, wenn meine Zahlung fehlschlägt?",
      a: "Bei einer gescheiterten Zahlung wird dein Abo automatisch pausiert und du kannst keine Matchas mehr einlösen. Aktualisiere einfach deine Zahlungsmethode und dein Abo läuft weiter.",
    },
    {
      q: "Ist die Membership personenbezogen?",
      a: "Ja, deine Membership ist an dein Kundenkonto gebunden und nicht übertragbar.",
    },
  ];

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: COLORS.background }}>
      {/* Header */}
      <header className="px-4 pt-6 pb-4 flex items-center gap-3">
        <motion.button 
          whileTap={{ scale: 0.9 }} 
          onClick={() => navigate("/landing")}
          className="p-2 rounded-xl"
          style={{ backgroundColor: COLORS.white }}
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5" style={{ color: COLORS.text }} />
        </motion.button>
        <h1 className="text-lg font-black" style={{ color: COLORS.text }}>
          Membership
        </h1>
      </header>

      {/* Hero - Premium, nicht billig */}
      <section className="px-5 py-6 text-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="flex justify-center gap-2 mb-4"
        >
          <MatchaIllustration size={50} />
          <CoffeeIllustration size={50} />
        </motion.div>
        
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl font-black mb-2"
          style={{ color: COLORS.text }}
        >
          Werde Teil der <span style={{ color: COLORS.green }}>Franky's</span> Familie
        </motion.h2>
        
        <p className="text-sm mb-3" style={{ color: COLORS.textMuted }}>
          Starte dein Ritual. Sichere dir deine Vorteile. Werde Stammgast.
        </p>
        
        <div className="flex justify-center gap-3 text-xs" style={{ color: COLORS.textMuted }}>
          <span className="flex items-center gap-1">
            <Check className="w-3 h-3" style={{ color: COLORS.green }} /> 3 Monate Kündigungsfrist
          </span>
          <span className="flex items-center gap-1">
            <Check className="w-3 h-3" style={{ color: COLORS.green }} /> Sofort aktiv
          </span>
        </div>
      </section>

      {/* Membership Cards */}
      <section className="px-5 py-4">
        <h3 className="text-lg font-bold mb-4" style={{ color: COLORS.text }}>
          Wähle deinen Club
        </h3>
        
        <div className="space-y-4">
          {/* Matcha Club - Premium Option */}
          <MembershipCard 
            membership="matcha" 
            isSelected={selectedPlan?.id === "matcha"}
            onSelect={setSelectedPlan}
            onJoin={handleJoin}
          />
          
          {/* Coffee Pass */}
          <MembershipCard
            membership="coffee"
            isSelected={selectedPlan?.id === "coffee"}
            onSelect={setSelectedPlan}
            onJoin={handleJoin}
          />

          {/* Pasta Flat */}
          <MembershipCard
            membership="pasta"
            isSelected={selectedPlan?.id === "pasta"}
            onSelect={setSelectedPlan}
            onJoin={handleJoin}
          />
        </div>
      </section>

      {/* How Membership Drinks Work */}
      <HowItWorksSection />

      {/* Mitglieder Lunch-Vorteile - als Zusatz */}
      <section className="px-5 py-2">
        <h3 className="text-sm font-bold mb-3 uppercase tracking-wide" style={{ color: COLORS.textMuted }}>
          Zusätzliche Vorteile
        </h3>
        <LunchPerksSection />
      </section>

      {/* FAQ */}
      <section className="px-5 py-6">
        <h3 className="text-lg font-bold mb-4" style={{ color: COLORS.text }}>
          Häufige Fragen
        </h3>
        
        <div className="space-y-2">
          {faqs.map((faq, i) => (
            <FAQItem
              key={i}
              question={faq.q}
              answer={faq.a}
              isOpen={openFAQ === i}
              onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
            />
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="px-5 py-4">
        <p className="text-center text-xs mb-4" style={{ color: COLORS.textMuted }}>
          3 Monate Kündigungsfrist • Sofort aktiv • Alle Filialen
        </p>
      </section>
    </div>
  );
}

export { MEMBERSHIPS, MembershipCard, LunchPerksSection, MatchaIllustration, CoffeeIllustration, COLORS };
